import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../api";
import { PageHeader, EmptyState, Modal, Field, Button, StatusBadge, inr } from "../components/UI";
import Typeahead from "../components/Typeahead";
import { ShoppingCart, Plus, Trash2, Receipt } from "lucide-react";
import { toast } from "sonner";

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ customer_id: "", items: [], notes: "", due_date: "", status: "pending" });
  const [filter, setFilter] = useState("all");
  const [predictions, setPredictions] = useState({}); // index -> prediction info

  const [selectedCustomerLabel, setSelectedCustomerLabel] = useState("");
  const load = async () => {
    setLoading(true);
    try {
      // Only load latest 200 orders + 100 products. Customer search is async via Typeahead.
      const [o, p] = await Promise.all([
        api.get("/orders?limit=200"),
        api.get("/products"),
      ]);
      setOrders(o.data); setProducts(p.data);
    } finally { setLoading(false); }
  };

  const searchCustomers = async (q) => {
    if (!q || q.length < 1) {
      // Show 50 most recent on empty query
      const r = await api.get("/customers?limit=50");
      return r.data;
    }
    const r = await api.get(`/customers?q=${encodeURIComponent(q)}&limit=50`);
    return r.data;
  };

  useEffect(() => { load(); }, []);

  const addItem = () => setForm({ ...form, items: [...form.items, { product_id: "", product_name: "", quantity: 1, unit_price: 0 }] });
  const updateItem = (i, k, v) => {
    const items = [...form.items];
    items[i] = { ...items[i], [k]: v };
    if (k === "product_id" && v) {
      const p = products.find((x) => x.id === v);
      if (p) { items[i].product_name = p.name; items[i].unit_price = p.default_price; }
    }
    setForm({ ...form, items });
    // fetch prediction for this customer+product
    const cust = form.customer_id;
    const pname = items[i].product_name;
    if (cust && pname && (k === "product_id" || k === "product_name")) {
      api.get(`/reorder/predict-next?customer_id=${encodeURIComponent(cust)}&product_name=${encodeURIComponent(pname)}`)
        .then((r) => setPredictions((prev) => ({ ...prev, [i]: r.data })))
        .catch(() => {});
    }
  };
  const removeItem = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });

  const total = form.items.reduce((s, i) => s + (Number(i.quantity) || 0) * (Number(i.unit_price) || 0), 0);

  const openNew = () => {
    setPredictions({});
    setForm({ customer_id: "", items: [{ product_id: "", product_name: "", quantity: 1, unit_price: 0 }], notes: "", due_date: "", status: "pending" });
    setSelectedCustomerLabel("");
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.customer_id) { toast.error("Select a customer"); return; }
    if (form.items.length === 0) { toast.error("Add at least one item"); return; }
    try {
      const payload = { ...form, items: form.items.map((i) => ({ ...i, quantity: Number(i.quantity), unit_price: Number(i.unit_price) })) };
      await api.post("/orders", payload);
      toast.success("Order created");
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const updateStatus = async (o, status) => {
    try { await api.patch(`/orders/${o.id}/status`, { status }); toast.success(`Status: ${status}`); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (o) => {
    if (!window.confirm(`Delete ${o.order_no}?`)) return;
    try { await api.delete(`/orders/${o.id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const convertToInvoice = async (o) => {
    try {
      const r = await api.post("/invoices", {
        order_id: o.id, customer_id: o.customer_id, items: o.items, tax_percent: 18.0, notes: `Generated from order ${o.order_no}`,
      });
      toast.success(`Invoice ${r.data.invoice_no} created`);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const filtered = filter === "all" ? orders : orders.filter((o) => o.status === filter);

  return (
    <div className="p-4 lg:p-8 max-w-7xl">
      <PageHeader
        title="Orders"
        subtitle="Track every print job from pending to delivered"
        action={<Button onClick={openNew} data-testid="add-order-btn"><Plus size={16}/>New Order</Button>}
      />

      <div className="flex gap-2 mb-5" data-testid="order-filters">
        {["all", "pending", "printing", "delivered", "cancelled"].map((s) => (
          <button key={s} data-testid={`order-filter-${s}`} onClick={() => setFilter(s)}
            className={`px-3 py-1.5 rounded text-xs uppercase tracking-wider font-semibold transition ${filter === s ? "bg-[#C1121F] text-white" : "bg-white border border-[#E5E7EB] text-[#475569] hover:border-[#C1121F]"}`}>
            {s}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-[#475569]">Loading…</div>
      ) : filtered.length === 0 ? (
        <EmptyState icon={ShoppingCart} title="No orders yet" hint="Create your first print job to start tracking progress."
          action={<Button onClick={openNew}><Plus size={16}/> New Order</Button>} />
      ) : (
        <div className="card-flat overflow-hidden">
          <table className="crm-table" data-testid="orders-table">
            <thead>
              <tr>
                <th>Order #</th>
                <th>Customer</th>
                <th>Items</th>
                <th className="text-right">Total</th>
                <th>Due</th>
                <th>Status</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((o) => (
                <tr key={o.id} data-testid={`order-row-${o.id}`}>
                  <td className="font-mono-num font-semibold text-[#C1121F]">{o.order_no}</td>
                  <td>{o.customer_name}</td>
                  <td className="text-[#475569] text-xs">{o.items.map((i) => `${i.product_name} ×${i.quantity}`).join(", ")}</td>
                  <td className="text-right font-mono-num font-semibold">{inr(o.total)}</td>
                  <td className="text-xs">{o.due_date || "—"}</td>
                  <td>
                    <select value={o.status} data-testid={`order-status-${o.id}`} onChange={(e) => updateStatus(o, e.target.value)}
                      className="text-xs px-2 py-1 rounded border border-[#E5E7EB] bg-white">
                      <option value="pending">pending</option>
                      <option value="printing">printing</option>
                      <option value="delivered">delivered</option>
                      <option value="cancelled">cancelled</option>
                    </select>
                  </td>
                  <td className="text-right">
                    <button onClick={() => convertToInvoice(o)} data-testid={`invoice-order-${o.id}`} className="p-1.5 text-[#475569] hover:text-[#C1121F]" title="Create Invoice"><Receipt size={15}/></button>
                    <button onClick={() => remove(o)} data-testid={`delete-order-${o.id}`} className="p-1.5 text-[#475569] hover:text-red-600"><Trash2 size={15}/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="New Order" maxWidth="max-w-3xl">
        <form onSubmit={save} className="space-y-4" data-testid="order-form">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Customer" required>
              <Typeahead
                loadOptions={searchCustomers}
                value={form.customer_id}
                selectedLabel={selectedCustomerLabel}
                onChange={(id, opt) => {
                  setForm({ ...form, customer_id: id });
                  setSelectedCustomerLabel(opt?.name || "");
                }}
                placeholder="Type to search customer (name, phone, city)…"
                required
                testid="order-customer"
                renderMeta={(c) => `${c.type || ""} ${c.phone ? "· " + c.phone : ""} ${c.city ? "· " + c.city : ""}`}
              />
            </Field>
            <Field label="Due Date">
              <input data-testid="order-due" type="date" value={form.due_date} onChange={(e) => setForm({...form, due_date: e.target.value})} className="input-base" />
            </Field>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">Items</label>
              <Button type="button" variant="secondary" onClick={addItem} data-testid="add-item-btn"><Plus size={14}/> Add item</Button>
            </div>
            <div className="space-y-2">
              {form.items.map((it, i) => (
                <div key={i} data-testid={`order-item-${i}`}>
                  <div className="grid grid-cols-12 gap-2 items-start">
                    <div className="col-span-7" data-testid={`order-item-${i}-product`}>
                      <Typeahead
                        options={products}
                        value={it.product_id || ""}
                        onChange={(id) => updateItem(i, "product_id", id)}
                        placeholder="Search product or type new name…"
                        testid={`product-typeahead-${i}`}
                        renderMeta={(p) => `${p.category || ""}${p.default_price ? " · ₹" + p.default_price : ""}`}
                      />
                      {!it.product_id && (
                        <input
                          placeholder="Or type custom product name…"
                          value={it.product_name}
                          onChange={(e) => updateItem(i, "product_name", e.target.value)}
                          required
                          data-testid={`order-item-${i}-name`}
                          className="input-base text-xs mt-1"
                        />
                      )}
                    </div>
                    <input type="number" min="1" placeholder="Qty" value={it.quantity} onChange={(e) => updateItem(i, "quantity", e.target.value)} className="input-base col-span-2" data-testid={`order-item-${i}-qty`}/>
                    <input type="number" step="0.01" placeholder="Price" value={it.unit_price} onChange={(e) => updateItem(i, "unit_price", e.target.value)} className="input-base col-span-2" data-testid={`order-item-${i}-price`}/>
                    <button type="button" onClick={() => removeItem(i)} className="col-span-1 text-red-500 hover:text-red-700 p-2" data-testid={`order-item-${i}-remove`}><Trash2 size={16}/></button>
                  </div>
                  {predictions[i] && predictions[i].found !== false && predictions[i].predicted_next_order_date && (
                    <div className="ml-1 mt-1.5 text-[11px] text-[#475569] bg-[#FEF2F2] border-l-2 border-[#C1121F] pl-2.5 py-1 rounded-r" data-testid={`prediction-${i}`}>
                      <span className="font-semibold text-[#C1121F]">🤖 AI:</span> Based on {predictions[i].order_count} past order{predictions[i].order_count !== 1 ? "s" : ""}, this customer reorders every <b>~{predictions[i].avg_gap_days} days</b>. Tentative next order: <b className="font-mono-num">{predictions[i].predicted_next_order_date}</b>
                    </div>
                  )}
                  {predictions[i] && predictions[i].found === false && (
                    <div className="ml-1 mt-1.5 text-[11px] text-[#94A3B8] pl-2.5 py-1" data-testid={`prediction-${i}-new`}>
                      🆕 First-time order — we&apos;ll start tracking the pattern.
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <Field label="Notes">
            <textarea value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} className="input-base" rows={2} />
          </Field>

          <div className="flex items-center justify-between pt-3 border-t border-[#E5E7EB]">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#94A3B8]">Order Total</div>
              <div className="font-mono-num font-bold text-2xl text-[#C1121F]">{inr(total)}</div>
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="secondary" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="save-order-btn">Create Order</Button>
            </div>
          </div>
        </form>
      </Modal>
    </div>
  );
}
